import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  ShoppingCart,
  CreditCard,
  Close,
  AddShoppingCart,
  CalendarToday,
  CurrencyRupee,
  LocalShipping,
  Payment,
  AssignmentTurnedIn,
  LocationOn,
  ShoppingBag
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import SecuredLayout from '../components/layouts/SecuredLayout';
import userService from '../services/userService';
import useCartGuard from '../hooks/useCartGuard';

const OrdersList = () => {
  const theme = useTheme();
  const { requireAuth } = useCartGuard();
  const [orders, setOrders] = useState([]);
  const [orderDetail, setOrderDetail] = useState(null);
  const [orderDialog, setOrderDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch orders from API
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await userService.getOrders();
        setOrders(response.data || []);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleViewItems = async (orderId) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (order) {
        // Fetch detailed order information
        const response = await userService.getOrderbyId(orderId);
        setOrderDetail(response.data || order);
        setOrderDialog(true);
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      // Fallback to basic order info if detailed fetch fails
      const order = orders.find(o => o.id === orderId);
      setOrderDetail(order);
      setOrderDialog(true);
    }
  };

  const handleAddToCart = async (orderId) => {
    if (!requireAuth()) return;
    try {
      await userService.orderToCart(orderId);
      // You can add a success notification here
      console.log(`Order ${orderId} items added to cart successfully`);
    } catch (err) {
      console.error('Error adding order to cart:', err);
      // You can add an error notification here
    }
  };

  const handleMakePayment = (orderId) => {
    // Simulate payment process
    console.log(`Processing payment for order ${orderId}`);
    // You can implement the actual payment logic here
  };

  const getStatusColor = (status) => {
    const colors = {
      'delivered': 'success',
      'shipped': 'info',
      'pending': 'warning',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'paid': 'success',
      'payment_pending': 'warning',
      'failed': 'error'
    };
    return colors[status] || 'default';
  };

  const getShippingStatusLabel = (status) => {
    const labels = {
      'not_sent': 'Not Shipped',
      'sent': 'Shipped',
      'failed': 'Failed'
    };
    return labels[status] || 'Not Shipped';
  };

  const getShippingStatusColor = (status) => {
    const colors = {
      'not_sent': 'default',
      'sent': 'info',
      'failed': 'error'
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `₹${numPrice.toFixed(2)}`;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <SecuredLayout 
        activeTab={2} 
        pageTitle="Orders List"
        contentCols={10}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      </SecuredLayout>
    );
  }

  if (error) {
    return (
      <SecuredLayout 
        activeTab={2} 
        pageTitle="Orders List"
        contentCols={10}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </SecuredLayout>
    );
  }

  return (
    <SecuredLayout 
      activeTab={2} 
      pageTitle="Orders List"
      contentCols={10}
    >
      {orders.length === 0 ? (
        <Alert severity="info">
          No orders found. Start shopping to see your orders here!
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order Id</TableCell>
                <TableCell align="center">Ordered Date</TableCell>
                <TableCell align="center">Order Status</TableCell>
                <TableCell align="center">Payment</TableCell>
                <TableCell align="center">Amount</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>{order.id}</TableCell>
                  <TableCell align="center">{formatDate(order.created_at)}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={order.status.toUpperCase()} 
                      color={getStatusColor(order.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={order.payment_status.toUpperCase()} 
                      color={getPaymentStatusColor(order.payment_status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{formatPrice(order.total_price)}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="View Items">
                        <IconButton
                          color="success"
                          onClick={() => handleViewItems(order.id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Add to Cart">
                        <IconButton
                          color="primary"
                          onClick={() => handleAddToCart(order.id)}
                        >
                          <AddShoppingCart />
                        </IconButton>
                      </Tooltip>

                      {order.payment_status === 'payment_pending' && (
                        <Tooltip title="Click to Pay">
                          <IconButton
                            color="error"
                            onClick={() => handleMakePayment(order.id)}
                          >
                            <CreditCard />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={orders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Order Details Dialog */}
        <Dialog
          open={orderDialog}
          onClose={() => setOrderDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, overflow: 'hidden' }
          }}
        >
          {/* Gradient Header */}
          <Box sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.darker || '#333968'} 0%, ${theme.palette.primary.main} 100%)`,
            px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                Order #{orderDetail?.id}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.75) }}>
                Placed on {orderDetail && formatDate(orderDetail.created_at)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip
                label={orderDetail?.status?.toUpperCase().replace(/_/g, ' ')}
                size="small"
                sx={{
                  backgroundColor: alpha('#fff', 0.2),
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: 0.5
                }}
              />
              <IconButton
                onClick={() => setOrderDialog(false)}
                sx={{ color: '#fff', '&:hover': { backgroundColor: alpha('#fff', 0.15) } }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>

          <DialogContent sx={{ p: 3 }}>
            {orderDetail && (
              <>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { label: 'Order Date', value: formatDate(orderDetail.created_at), icon: <CalendarToday fontSize="small" />, color: theme.palette.primary.main },
                    { label: 'Total Price', value: formatPrice(orderDetail.total_price), icon: <CurrencyRupee fontSize="small" />, color: theme.palette.success.main },
                    { label: 'Order Status', value: orderDetail.status.replace(/_/g, ' ').toUpperCase(), icon: <AssignmentTurnedIn fontSize="small" />, color: theme.palette.info.main },
                    { label: 'Payment', value: orderDetail.payment_status.replace(/_/g, ' ').toUpperCase(), icon: <Payment fontSize="small" />, color: orderDetail.payment_status === 'payment_complete' ? theme.palette.success.main : theme.palette.warning.main },
                    { label: 'Shipping', value: getShippingStatusLabel(orderDetail.shiprocket_status), icon: <LocalShipping fontSize="small" />, color: theme.palette.secondary.main },
                  ].map((card, i) => (
                    <Grid item xs={6} sm={4} md={2.4} key={i}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: alpha(card.color, 0.25),
                          backgroundColor: alpha(card.color, 0.04),
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          '&:hover': { borderColor: alpha(card.color, 0.5), transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${alpha(card.color, 0.15)}` }
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ color: card.color, mb: 0.75, display: 'flex', justifyContent: 'center' }}>
                            {card.icon}
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                            {card.label}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {card.value}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Delivery Address */}
                {orderDetail.delivery_address && (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 3, borderRadius: 2,
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                      backgroundColor: alpha(theme.palette.primary.main, 0.03)
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.darker }}>
                          Delivery Address
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {orderDetail.delivery_address.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {orderDetail.delivery_address.addressline1}
                        {orderDetail.delivery_address.addressline2 && `, ${orderDetail.delivery_address.addressline2}`}
                        <br />
                        {orderDetail.delivery_address.area}, {orderDetail.delivery_address.city}, {orderDetail.delivery_address.state} — {orderDetail.delivery_address.postal_code}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Order Items */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ShoppingBag sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.darker }}>
                    Order Items
                  </Typography>
                </Box>
                {orderDetail.items && orderDetail.items.length > 0 ? (
                  <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
                            <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.darker, py: 1.5 }}>Product</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: theme.palette.primary.darker }}>Qty</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: theme.palette.primary.darker }}>M.R.P</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: theme.palette.primary.darker }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orderDetail.items.map((item, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor: index % 2 === 0 ? 'transparent' : alpha(theme.palette.primary.main, 0.02),
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) }
                              }}
                            >
                              <TableCell sx={{ py: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Box sx={{
                                    width: 36, height: 36, borderRadius: 1.5,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0
                                  }}>
                                    <ShoppingBag sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                                  </Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {item.product_name || item.otc_name || 'Unknown Product'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={item.quantity} size="small" variant="outlined" sx={{ fontWeight: 700, minWidth: 32 }} />
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 500 }}>{formatPrice(item.price)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{formatPrice(item.subtotal)}</TableCell>
                            </TableRow>
                          ))}
                          {/* Total Row */}
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
                            <TableCell colSpan={2} sx={{ fontWeight: 700, color: theme.palette.primary.darker }}>
                              Grand Total
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              {formatPrice(orderDetail.items.reduce((sum, item) => sum + parseFloat(item.subtotal || 0), 0))}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, color: theme.palette.primary.dark, fontSize: '1rem' }}>
                              {formatPrice(orderDetail.total_price)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No items found for this order.
                  </Alert>
                )}

                {/* Payment Notice */}
                {orderDetail.payment_status === 'payment_pending' && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      mt: 2.5, borderRadius: 2, borderColor: theme.palette.warning.main,
                      backgroundColor: alpha(theme.palette.warning.main, 0.06)
                    }}
                    action={
                      <Button
                        color="warning" variant="contained" size="small"
                        onClick={() => handleMakePayment(orderDetail.id)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Pay Now
                      </Button>
                    }
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Payment is pending for this order. Complete payment to confirm.
                    </Typography>
                  </Alert>
                )}

                {/* Add to Cart */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddShoppingCart />}
                    onClick={() => handleAddToCart(orderDetail.id)}
                    sx={{
                      textTransform: 'none', fontWeight: 600, borderRadius: 2,
                      px: 3, py: 1, borderWidth: 2,
                      '&:hover': { borderWidth: 2, backgroundColor: alpha(theme.palette.primary.main, 0.06) }
                    }}
                  >
                    Add All Items to Cart
                  </Button>
                </Box>
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setOrderDialog(false)}
              variant="contained"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
    </SecuredLayout>
  );
};

export default OrdersList;
